import clsx from 'clsx';

import { List } from './List';
import {
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemDetail,
    ListItemLeading,
    type ListItemProps,
    ListItemTrailing,
} from './ListItem';

type ListItemPart = 'indicator' | 'detail' | 'controls';

interface ListSkeletonProps extends Pick<ListItemProps, 'size' | 'align'> {
    items?: number;
    excludeParts?: ListItemPart[];
    className?: string;
    indicator?: React.ReactNode;
}

/**
 * A skeleton list component with a pulsating animation. To be used where more appropriate then the
 * common LoadingIndicator.
 *
 * @param props.items The number of skeleton items to render.
 * @param props.excludeParts An array of parts to exclude from the skeleton items.
 */
export function ListSkeleton(props: ListSkeletonProps) {
    const {
        items = 3,
        size = 'medium',
        align = 'center',
        excludeParts = [],
        className,
        indicator,
    } = props;

    return (
        <div
            role="status"
            aria-busy
            className={clsx('size-full motion-safe:animate-pulse', className)}
        >
            <List
                items={[...Array(items).keys()]}
                renderItem={(item) => (
                    <ListItemSkeleton
                        key={item.index}
                        size={size}
                        align={align}
                        excludeParts={excludeParts}
                        indicator={indicator}
                    />
                )}
            />
        </div>
    );
}

/**
 * Skeleton for a single list item.
 */
export function ListItemSkeleton(
    props: { excludeParts?: ListItemPart[]; indicator?: React.ReactNode } & Pick<
        ListItemProps,
        'size' | 'align'
    >
) {
    const { excludeParts = [], size, align, indicator } = props;

    return (
        <ListItem size={size} align={align}>
            {excludeParts.includes('indicator') ? null : (
                <ListItemLeading>
                    {indicator || (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary" />
                    )}
                </ListItemLeading>
            )}

            <ListItemBody>
                <ListItemDescription>
                    <div className="mb-2 h-3 w-28 rounded-full bg-secondary" />
                </ListItemDescription>
                {excludeParts.includes('detail') ? null : (
                    <ListItemDetail>
                        <div className="h-2 w-14 rounded-full bg-secondary" />
                    </ListItemDetail>
                )}
            </ListItemBody>

            {excludeParts.includes('controls') ? null : (
                <ListItemTrailing when="always">
                    <div className="h-2 w-10 rounded-full bg-secondary" />
                </ListItemTrailing>
            )}
        </ListItem>
    );
}
